import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramResult,
  CropFCPO,
  INCENTIVE_NAME,
  PRACTICE_SLUG,
  PracticeSlug,
  RequirementLevel,
} from "models";

type FormInput = "cropName" | "dates" | "yield" | "fertilizerManagement" | "tillageDepth" | Exclude<PracticeSlug, "crops">;
export type InputRequirementConfig = { [k in FormInput]?: RequirementLevel };

export const isRequired = (level?: RequirementLevel) => level === "requiredForProgram" || level === "requiredForSaving";
export const isRequiredForSaving = (level?: RequirementLevel) => level === "requiredForSaving";
// NOTE: If the input key does not exist in the requirement config we completely hide the input even if optional inputs are shown
export const shouldShowInput = (level?: RequirementLevel, optionalInputsAreShown?: boolean) =>
  isRequired(level) || (level === "optional" && optionalInputsAreShown) || level === "optionalAlwaysShown";

/**
 * Function which returns the configuration which inputs are required for saving form, required for program or optional.
 * If the configuration does not include an input key, it will be hidden always.
 *
 * @param carbonProgram carbon program configuration needed to check if process based model is used as algorithm
 * @param carbonProgramResult return of the carbon runner for one field
 * @param crop currently selected crop needed to check crop type
 * @returns requirement configuration -> object with input key mapping to requirement level
 */
export const getInputRequirementConfig = (
  carbonProgram: CarbonProgram | null,
  carbonProgramResult: CarbonProgramResult | null,
  crop: CropFCPO,
  fieldIsEnrolled: boolean
): InputRequirementConfig | null => {
  // Default config has other practices as optional (even if they are not even part of the program, because they don't affect payout)
  const config: InputRequirementConfig = {
    cropName: "requiredForSaving",
    dates: "requiredForSaving",
    mineralFertilizers: "optional",
    organicFertilizers: "optional",
    seedings: "optional",
    tillages: "optional",
  };

  if (crop.cropType === "UNDERSOWING") {
    return {
      cropName: "requiredForSaving",
      dates: "requiredForSaving",
    };
  }

  if (carbonProgram && carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) {
    const practiceMap = new Map(carbonProgram?.practices?.map((p) => [p.slug, p]));
    const organicFertilizerConfig = practiceMap.get(PRACTICE_SLUG.ORGANIC_FERTILIZERS);
    const tillageConfig = practiceMap.get(PRACTICE_SLUG.TILLAGES);

    const conservationTillageIncentive = tillageConfig?.incentives?.find(({ name }) => name === INCENTIVE_NAME.conservationTillage);
    const organicFertilizerIncentive = organicFertilizerConfig?.incentives?.find(({ name }) => name === INCENTIVE_NAME.fourR);
    const organicFertilizerIncentiveConfig = organicFertilizerIncentive
      ? {
          // 4R should never be optional, but completely hidden if it doesn't exist
          ...(crop.cropType === CROP_TYPE_FCPO.MAIN && !!organicFertilizerIncentive?.requirementLevel
            ? { fertilizerManagement: organicFertilizerIncentive.requirementLevel }
            : {}),
        }
      : {};

    return {
      // practices
      ...config,
      ...(organicFertilizerConfig?.required ? { organicFertilizers: "requiredForProgram" } : {}),
      ...(crop.cropType === CROP_TYPE_FCPO.MAIN ? { yield: "requiredForProgram" } : {}),

      // incentives
      ...organicFertilizerIncentiveConfig,
      ...(conservationTillageIncentive ? { tillageDepth: "requiredForSaving", tillages: tillageConfig ? "requiredForProgram" : "optional" } : {}),
    };
  }

  if (carbonProgramResult && "error" in carbonProgramResult) return config;

  // If there is no carbon program (in that case we only should have noe carbon program result)  we just fall back to showing everything
  // If field is not enrolled we also want to show all inputs
  if (!carbonProgram || !carbonProgramResult || !fieldIsEnrolled) {
    return {
      cropName: "requiredForSaving",
      dates: "requiredForSaving",
      mineralFertilizers: "requiredForProgram",
      organicFertilizers: "requiredForProgram",
      seedings: "requiredForProgram",
      tillages: "requiredForProgram",
      tillageDepth: "requiredForProgram",
      ...(crop.cropType === CROP_TYPE_FCPO.MAIN ? { yield: "optionalAlwaysShown" } : {}),
    };
  }

  if (crop.cropType === CROP_TYPE_FCPO.MAIN) {
    config.yield = "optionalAlwaysShown";
  }

  // Check if carbon program has 4R as incentive
  const organicFertilizerConfig = carbonProgram?.practices?.find(({ slug }) => slug === PRACTICE_SLUG.ORGANIC_FERTILIZERS);
  const incentive4R = organicFertilizerConfig?.incentives?.find(({ name }) => name === INCENTIVE_NAME.fourR);

  // It should never be optional, but completely hidden if there is no 4R incentive
  if (incentive4R && crop.cropType === CROP_TYPE_FCPO.MAIN) {
    config.fertilizerManagement = incentive4R.requirementLevel;
  }

  // Check if carbon program has conservation tillage as incentive
  const tillageConfig = carbonProgram?.practices?.find(({ slug }) => slug === PRACTICE_SLUG.TILLAGES);
  const hasConservationTillageIncentive = tillageConfig?.incentives?.some(({ name }) => name === INCENTIVE_NAME.conservationTillage);

  // It should never be optional, but completely hidden if there is no tillage incentive
  if (hasConservationTillageIncentive) {
    config.tillageDepth = "requiredForSaving";
  }

  // Loop through practice keys and figure out which requirement they have (this will then override the optional state possibly)
  (Object.keys(carbonProgramResult.fieldOutcomes) as PracticeSlug[]).forEach((slug) => {
    if (slug === PRACTICE_SLUG.CROPS) {
      return; // No need to look at crops, because the crop name and dates are already defined as requiredForSaving anyway
    }

    // If the carbon runner result has the practice and it is set to required due to one of the reasons, we set it to "requiredForProgram"
    // I suppose if the practice is already participating we want to show it anyway
    const fieldOutcomesOfPractice = carbonProgramResult.fieldOutcomes[slug];
    config[slug] =
      Object.values(fieldOutcomesOfPractice?.required ?? {}).some((isRequired) => isRequired) || fieldOutcomesOfPractice?.isParticipating
        ? "requiredForProgram"
        : "optional";
  });

  return config;
};
